import type {
  AssetClass, DCAStream, InvestmentPlan, PendingTransfer, PlanAccountRule, PlanReserve,
} from '../services/investment-plan-service';

/**
 * The Investment plan form's state and its validation (issue #31).
 *
 * Numbers are kept as strings while editing so a half-typed "12." is not
 * coerced away; validation parses them. The server validates again -- this
 * is for immediate feedback, not trust.
 */
export interface PlanForm {
  enabled: boolean;
  targets: Record<AssetClass, string>;
  bandPts: string;
  cashFloor: string;
  caps: { crypto: string; commodity: string; private: string; singleStock: string };
  /** Kept as API objects; the page edits amount/cadence in place. */
  streams: DCAStream[];
  reserves: PlanReserve[];
  pendingTransfers: PendingTransfer[];
  dcaOn: boolean;
  dca: {
    pool: string;
    startDate: string;
    rate: string;
    rateUnit: 'trading_day' | 'month';
    source: 'plan' | 'external';
    targetEnd: string;
    autoPool: boolean;
  };
  excluded: string[];
  watchlist: string;
  expiryDays: string;
  signals: boolean;
  enrich: boolean;
}

export const EMPTY_FORM: PlanForm = {
  enabled: false,
  targets: { equity: '80', fixed_income: '15', cash: '5', crypto: '0', commodity: '0', private: '0', other: '0' },
  bandPts: '5',
  cashFloor: '0',
  caps: { crypto: '', commodity: '', private: '', singleStock: '' },
  streams: [],
  reserves: [],
  pendingTransfers: [],
  dcaOn: false,
  dca: { pool: '', startDate: '', rate: '', rateUnit: 'trading_day', source: 'plan', targetEnd: '', autoPool: false },
  excluded: [],
  watchlist: '',
  expiryDays: '14',
  signals: true,
  enrich: false,
};

const n = (s: string | number | undefined | null): number => {
  const v = typeof s === 'number' ? s : parseFloat(String(s ?? ''));
  return Number.isFinite(v) ? v : NaN;
};

/** Sum of the target percentages, rounded to 2 dp to absorb float noise. */
export function targetSum(t: Record<AssetClass, string>): number {
  return Math.round(Object.values(t).reduce((s, v) => s + (Number.isFinite(n(v)) ? n(v) : 0), 0) * 100) / 100;
}

/** Field -> message. Empty when the form is valid. */
export function validatePlanForm(f: PlanForm): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const [cls, v] of Object.entries(f.targets)) {
    const x = n(v === '' ? '0' : v);
    if (!Number.isFinite(x) || x < 0 || x > 100) errs[`targets.${cls}`] = 'Between 0 and 100';
  }
  const sum = targetSum(f.targets);
  if (sum !== 100) errs.targets = `Targets must add up to 100% (now ${sum}%)`;
  const band = n(f.bandPts);
  if (!(band > 0 && band <= 50)) errs.bandPts = 'Band must be more than 0 and at most 50 points';
  const floor = n(f.cashFloor === '' ? '0' : f.cashFloor);
  if (!(floor >= 0)) errs.cashFloor = 'Cash floor cannot be negative';
  for (const [k, v] of Object.entries(f.caps)) {
    if (v !== '' && !(n(v) > 0 && n(v) <= 100)) errs[`caps.${k}`] = 'Between 0 and 100';
  }
  const exp = n(f.expiryDays);
  if (!(Number.isInteger(exp) && exp >= 1 && exp <= 90)) errs.expiryDays = '1 to 90 days';
  if (f.dcaOn) {
    if (!f.dca.autoPool && !(n(f.dca.pool) >= 0)) errs['dca.pool'] = 'Pool amount is required';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.dca.startDate)) errs['dca.startDate'] = 'Start date is required';
    if (f.dca.source === 'plan' && f.streams.length === 0 && !(n(f.dca.rate) > 0)) errs['dca.rate'] = 'Rate must be positive';
    f.streams.forEach((s, i) => {
      if (s.amount !== null && s.amount !== '' && !(n(s.amount) >= 0)) errs[`streams.${i}`] = 'Amount must be 0 or more (blank = unknown)';
    });
  }
  return errs;
}

/**
 * Form -> API body. Zero targets are dropped (the server ignores them too).
 * `existing` keeps account rules the form does not edit (a class pinned via
 * the API, a role) while the form's exclusions are applied on top.
 */
export function formToPlan(f: PlanForm, existing: PlanAccountRule[] = []): InvestmentPlan {
  const targets: InvestmentPlan['targets'] = {};
  for (const [cls, v] of Object.entries(f.targets) as [AssetClass, string][]) {
    if (n(v) > 0) targets[cls] = n(v);
  }
  const caps: InvestmentPlan['caps'] = {};
  if (f.caps.crypto !== '') caps.crypto_pct = n(f.caps.crypto);
  if (f.caps.commodity !== '') caps.commodity_pct = n(f.caps.commodity);
  if (f.caps.private !== '') caps.private_pct = n(f.caps.private);
  if (f.caps.singleStock !== '') caps.single_stock_pct = n(f.caps.singleStock);
  return {
    targets,
    band_pts: n(f.bandPts),
    caps,
    cash_floor: n(f.cashFloor === '' ? '0' : f.cashFloor),
    reserves: f.reserves,
    pending_transfers: f.pendingTransfers,
    dca: f.dcaOn
      ? {
          pool_amount: n(f.dca.pool),
          start_date: f.dca.startDate,
          rate: f.dca.source === 'plan' ? n(f.dca.rate) : n(f.dca.rate || '0') || 0,
          rate_unit: f.dca.rateUnit,
          source: f.dca.source,
          ...(f.dca.targetEnd ? { target_end_date: f.dca.targetEnd } : {}),
          auto_pool: f.dca.autoPool,
          streams: f.streams.map((s) => ({ ...s, amount: s.amount === '' || s.amount === null ? null : n(s.amount) })),
        }
      : null,
    accounts: mergeAccountRules(existing, f.excluded),
    watchlist: f.watchlist.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean),
    recommendation_expiry_days: n(f.expiryDays),
    disable_signals: !f.signals,
    enrich_reasons_with_llm: f.enrich,
  };
}

/** API plan -> form, keeping account rules other than exclusions intact elsewhere. */
export function planToForm(enabled: boolean, p: InvestmentPlan | null): PlanForm {
  if (!p) return { ...EMPTY_FORM, targets: { ...EMPTY_FORM.targets } };
  const s = (v: unknown) => (v === undefined || v === null ? '' : String(v));
  const t = { equity: '0', fixed_income: '0', cash: '0', crypto: '0', commodity: '0', private: '0', other: '0' } as Record<AssetClass, string>;
  for (const [k, v] of Object.entries(p.targets ?? {})) t[k as AssetClass] = s(v);
  return {
    enabled,
    targets: t,
    bandPts: s(p.band_pts) || '5',
    cashFloor: s(p.cash_floor) || '0',
    caps: { crypto: s(p.caps?.crypto_pct), commodity: s(p.caps?.commodity_pct), private: s(p.caps?.private_pct), singleStock: s(p.caps?.single_stock_pct) },
    streams: p.dca?.streams ?? [],
    reserves: p.reserves ?? [],
    pendingTransfers: p.pending_transfers ?? [],
    dcaOn: !!p.dca,
    dca: {
      pool: s(p.dca?.pool_amount),
      startDate: s(p.dca?.start_date),
      rate: s(p.dca?.rate),
      rateUnit: p.dca?.rate_unit ?? 'trading_day',
      source: p.dca?.source ?? 'plan',
      targetEnd: s(p.dca?.target_end_date),
      autoPool: !!p.dca?.auto_pool,
    },
    excluded: (p.accounts ?? []).filter((a) => a.include === false).map((a) => a.account_id),
    watchlist: (p.watchlist ?? []).join(', '),
    expiryDays: s(p.recommendation_expiry_days) || '14',
    signals: !p.disable_signals,
    enrich: !!p.enrich_reasons_with_llm,
  };
}

/** "on track" etc. with a tone for chips. */
export function dcaStatusLabel(status: string): { label: string; tone: 'success' | 'warning' | 'info' | 'default' } {
  switch (status) {
    case 'on_track': return { label: 'On track', tone: 'success' };
    case 'behind': return { label: 'Behind', tone: 'warning' };
    case 'ahead': return { label: 'Ahead', tone: 'info' };
    case 'complete': return { label: 'Complete', tone: 'success' };
    case 'not_started': return { label: 'Not started', tone: 'default' };
    case 'unverified': return { label: 'No recent data', tone: 'default' };
    default: return { label: 'Not configured', tone: 'default' };
  }
}

function mergeAccountRules(existing: PlanAccountRule[], excluded: string[]): PlanAccountRule[] {
  const out = new Map<string, PlanAccountRule>();
  // Exclusions are the form's to decide; an explicit include (a hidden
  // account brought in via the API) and pinned classes/roles are kept.
  for (const r of existing) {
    out.set(r.account_id, r.include === false ? { ...r, include: undefined } : r);
  }
  for (const id of excluded) out.set(id, { ...(out.get(id) ?? { account_id: id }), include: false });
  return [...out.values()].filter((r) => r.include !== undefined || !!r.asset_class || !!r.role);
}
